import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { apiPath, firstListItem } from '@/constants/defaultValues';
import { Delete, Get, PostBlob, Post,Put } from '@/api/api-middleware';
import { getCompanyID, downloadFile } from '@/helpers/Utils';

const initialState = {
  policyDynamicFormList: [],
  policyOvertimeDynamicFormList: [],
  exceptionListAll: [],
  exceptionDetail: {},
  addUpdateDeleteExceptionResponse: {},
  policyAddUpdateDelete: {},
  policyAssignmentAddUpdateDelete: {},
  policyItem: {},
  policyAssignedItemsList: [],
  notification: { show: false, code: '' },
  loading: false,
  error: '',
};

export const fetchPolicyDynamicForm = createAsyncThunk(
  'policy/fetchPolicyDynamicForm',
  async (params, thunkAPI) => {
    try {
      const response = await Get({
        url: `${apiPath}api/Lookup/System/GetDynamicFormSpec/${params}/${getCompanyID()}`,
      });
      return response?.data?.data;
    } catch (error) {
      return thunkAPI.rejectWithValue(error.message);
    }
  }
);

export const ExportExceptionList = async (params, thunkAPI) => {
  try {
    const url = `${apiPath}${'api/Reporting/ExportExceptionList'}`;
    const response = await PostBlob({
      url: url,
      data: params,
    });
    if (response) {
      downloadFile(response);
      return 1;
    } else {
      return -1;
    }
  } catch (error) {
    console.log(error);
    return -1;
  }
  return true;
};
export const addNewException = createAsyncThunk(
  'exception/addNewException',
  async (params, thunkAPI) => {
    try {
      const response =
        params?.id
          ? await Put({
              url: `${apiPath}api/Lookup/EmployeeException/Update/${params.id}`,
              data: params,
            })
          : await Post({
              url: `${apiPath}api/Lookup/EmployeeException/Add`,
              data: params,
            });

      return response?.data?.errCode === '200'
        ? response.data?.data
        : response?.data?.errCode === '555'
        ? response.data
        : response?.data?.errCode === '400' && response?.data?.data === '-10'
        ? thunkAPI.rejectWithValue(response?.data?.data)
        : thunkAPI.rejectWithValue('500');
    } catch (error) {
      return thunkAPI.rejectWithValue(error.message);
    }
  }
);

export const deleteException = createAsyncThunk(
  'exception/deleteException',
  async (params, thunkAPI) => {
    try {
      const response = await Delete({
        url: `${apiPath}${'api/Lookup/EmployeeException/Delete'}/${params}`,
      });
      const responseCode = response?.data?.errCode;
      if (responseCode === '200') {
        return response.data?.data;
      } else thunkAPI.rejectWithValue('Internal server Error');
    } catch (error) {
      return thunkAPI.rejectWithValue(error.message);
    }
  }
);
export const getAllEmployeeExceptions = createAsyncThunk(
  'exception/getAllEmployeeExceptions',
  async (params, thunkAPI) => {
    try {
      const response = await Post({
        url: `${apiPath}${'api/Lookup/EmployeeException/GetAll'}`,
        data: params,
      });
      return response?.data?.data;
    } catch (error) {
      return thunkAPI.rejectWithValue(error.message);
    }
  }
);

export const getExceptionById = createAsyncThunk(
  'policy/getExceptionById',
  async (params, thunkAPI) => {
    try {
      const response = await Get({
        url: `${apiPath}api/Lookup/EmployeeException/GetByID/${params}`,
      });
      return response?.data?.data;
    } catch (error) {
      return thunkAPI.rejectWithValue(error.message);
    }
  }
);

export const getPolicyById = createAsyncThunk(
  'policy/getPolicyById',
  async (params, thunkAPI) => {
    try {
      const response = await Get({
        url: `${apiPath}api/Lookup/TAPolicy/GetByID/${params}`,
      });
      return response?.data?.data;
    } catch (error) {
      return thunkAPI.rejectWithValue(error.message);
    }
  }
);

export const addPolicyAssignment = createAsyncThunk(
  'policy/addPolicyAssignment',
  async (params, thunkAPI) => {
    try {
      const response = await Post({
        url: `${apiPath}${'api/Operations/PolicyAssignment/Add'}`,
        data: params,
      });
      return response?.data?.data;
    } catch (error) {
      return thunkAPI.rejectWithValue(error.message);
    }
  }
);

export const fetchAllAssignedPolicies = createAsyncThunk(
  'policy/fetchAllAssignedPolicies',
  async (params, thunkAPI) => {
    try {
      const response = await Post({
        url: `${apiPath}${'api/Operations/PolicyAssignment/Get'}`,
        data: params,
      });
      return response?.data?.data;
    } catch (error) {
      return thunkAPI.rejectWithValue(error.message);
    }
  }
);

const exceptionSlice = createSlice({
  name: 'exception',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchPolicyDynamicForm.pending, (state) => {
        state.loading = true;
        state.notification = { show: false, code: '' };
      })
      .addCase(fetchPolicyDynamicForm.fulfilled, (state, action) => {
        state.loading = false;
        if (action.payload?.find((x) => x.formCode === 'TAPolicy')) {
          state.policyDynamicFormList = action.payload;
        } else if (
          action.payload?.find((x) => x.formCode === 'TAPolicyOverTime')
        ) {
          state.policyOvertimeDynamicFormList = action.payload;
        }
        state.error = '';
      })
      .addCase(fetchPolicyDynamicForm.rejected, (state, action) => {
        state.loading = false;
        state.policyDynamicFormList = [];
        state.policyOvertimeDynamicFormList = [];
        state.error = action.payload ?? 'An error occurred';
      });

    builder
      .addCase(addNewException.pending, (state) => {
        state.loading = true;
        state.notification = { show: false, code: '' };
      })
      .addCase(addNewException.fulfilled, (state, action) => {
        state.loading = false;
        if(action.payload){
          if(action?.payload?.errCode === '555'){
            state.addUpdateDeleteExceptionResponse = { status: false };
            state.notification = { show: true, code: '555' };
            state.error = '';
          }else{
            state.addUpdateDeleteExceptionResponse = { status: true };
            state.notification = { show: true, code: '200' };
            state.error = '';
          }
        }
      })
      .addCase(addNewException.rejected, (state, action) => {
        state.loading = false;
        state.addUpdateDeleteExceptionResponse = {};
        state.error = action.payload ?? 'An error occurred';
      });
      
    builder
      .addCase(deleteException.pending, (state) => {
        state.loading = true;
        state.notification = { show: false, code: '' };
      })
      .addCase(deleteException.fulfilled, (state, action) => {
        state.loading = false;
        state.notification = { show: true, code: '200' };
        state.error = '';
      })
      .addCase(deleteException.rejected, (state, action) => {
        state.loading = false;
        state.notification = { show: true, code: '500' };
        state.error = action.payload ?? 'An error occurred';
      });

    builder
      .addCase(getAllEmployeeExceptions.pending, (state) => {
        state.loading = true;
        state.notification = { show: false, code: '' };
      })
      .addCase(getAllEmployeeExceptions.fulfilled, (state, action) => {
        state.loading = false;
        state.exceptionListAll = action.payload;
        state.error = '';
      })
      .addCase(getAllEmployeeExceptions.rejected, (state, action) => {
        state.loading = false;
        state.exceptionListAll = [];
        state.error = action.payload ?? 'An error occurred';
      });
    builder
      .addCase(getExceptionById.pending, (state) => {
        state.loading = true;
        state.notification = { show: false, code: '' };
        state.exceptionDetail = {};
      })
      .addCase(getExceptionById.fulfilled, (state, action) => {
        state.loading = false;
        state.exceptionDetail = action.payload;
        state.error = '';
      })
      .addCase(getExceptionById.rejected, (state, action) => {
        state.loading = false;
        state.exceptionDetail = {};
        state.error = action.payload ?? 'An error occurred';
      });  

    builder
      .addCase(getPolicyById.pending, (state) => {
        state.loading = true;
        state.notification = { show: false, code: '' };
      })
      .addCase(getPolicyById.fulfilled, (state, action) => {
        state.loading = false;
        state.policyItem = action.payload;
        state.error = '';
      })
      .addCase(getPolicyById.rejected, (state, action) => {
        state.loading = false;
        state.policyItem = {};
        state.error = action.payload ?? 'An error occurred';
      });

    builder
      .addCase(addPolicyAssignment.pending, (state) => {
        state.loading = true;
        state.notification = { show: false, code: '' };
      })
      .addCase(addPolicyAssignment.fulfilled, (state, action) => {
        state.loading = false;
        state.policyAssignmentAddUpdateDelete = action.payload;
        state.notification = { show: true, code: '200' };
        state.error = '';
      })
      .addCase(addPolicyAssignment.rejected, (state, action) => {
        state.loading = false;
        state.policyAssignmentAddUpdateDelete = {};
        state.error = action.payload ?? 'An error occurred';
      });

    builder
      .addCase(fetchAllAssignedPolicies.pending, (state) => {
        state.loading = true;
        state.notification = { show: false, code: '' };
      })
      .addCase(fetchAllAssignedPolicies.fulfilled, (state, action) => {
        state.loading = false;
        state.policyAssignedItemsList = action.payload;
        state.error = '';
      })
      .addCase(fetchAllAssignedPolicies.rejected, (state, action) => {
        state.loading = false;
        state.policyAssignedItemsList = {};
        state.error = action.payload ?? 'An error occurred';
      });
  },
});

export default exceptionSlice.reducer;
